import type { ReactElement } from 'react';

import { illustrations } from '../../assets/illustrations';
import { cn } from '../../lib/utils';
import { LegendDotItem } from '../../molecules/LegendDotItem';
import { MiniMetricItem } from '../../molecules/MiniMetricItem';
import { StackedSegmentBar } from '../../molecules/StackedSegmentBar';

/**
 * The stale half of the card: its own bar, its own legend row and a third tile.
 * It is a separate bar rather than a third segment of the duplicated one
 * because a stale file can also be a duplicate, and because the two shares are
 * taken over different wholes. Whether a measurement is fit to chart is the
 * consumer's call, expressed as a null `percent`.
 */
export interface StaleDuplicateDataBodyStale {
  /** 0..100, or null when the consumer has no measurement to chart; the bar is then omitted. */
  percent: number | null;
  /** Pre-formatted size + count strings for the Stale tile (wrapper formats; body never formats). */
  sizeLabel: string;
  countLabel: string;
  /** CSS color string (wrapper-resolved). */
  color: string;
  /** i18n-friendly strings. */
  legendLabel: string;
  descriptorLabel: string;
  /** Accessible label for the stale bar. */
  barAriaLabel?: string;
}

/**
 * Color string (CSS hex/rgb/hsl) — segments and dot consumers expect strings,
 * not Tailwind classes. The wrapper resolves these at module scope.
 */
export interface StaleDuplicateDataBodyProps {
  /** 0..100 — duplicated data percent of scanned size. (Remainder = max(0, 100 - duplicated).) */
  duplicatedPercent: number;

  /** Pre-formatted size + count strings for each tile (wrapper formats; body never formats). */
  total: { sizeLabel: string; countLabel: string };
  duplicated: { sizeLabel: string; countLabel: string };

  /** Omitted when the warehouse publishes no stale rollup for the scope; the card is duplicate-only then. */
  stale?: StaleDuplicateDataBodyStale;

  /** CSS color strings (wrapper-resolved). */
  duplicatedColor: string;
  remainderColor: string;
  totalDotColor: string;

  /** i18n-friendly strings (organism is consumer-agnostic per @dawn/ui agnosticism). */
  duplicatedLegendLabel: string;
  totalDescriptorLabel: string;
  duplicatedDescriptorLabel: string;
  /** Accessible label for the duplicated bar. Defaults to English fallback when omitted. */
  barAriaLabel?: string;

  className?: string;
}

/**
 * Populated body for the Stale & Duplicate Data widget card.
 *
 * Layout: one 30-px `StackedSegmentBar` per measure, each with its own legend
 * row (`LegendDotItem` gap=8), then a row of `MiniMetricItem` tiles for Total /
 * Duplicated / [Stale]. Without `stale` the card is one bar and two tiles.
 *
 * Figma 5:23974 draws duplicated and stale as adjacent segments of a single
 * bar summing to 100. They overlap (a stale file can also be a duplicate) and
 * their shares are taken over different wholes, so each gets its own bar.
 *
 * Body is consumer-agnostic — the wrapper is the only place API shape meets
 * organism shape (resolves colors, formats numbers).
 */
export function StaleDuplicateDataBody({
  duplicatedPercent,
  total,
  duplicated,
  stale,
  duplicatedColor,
  remainderColor,
  totalDotColor,
  duplicatedLegendLabel,
  totalDescriptorLabel,
  duplicatedDescriptorLabel,
  barAriaLabel,
  className,
}: StaleDuplicateDataBodyProps): ReactElement {
  return (
    <div
      data-testid="stale-duplicate-data-body"
      className={cn('flex flex-col gap-6 font-outfit text-dawn-black', className)}
    >
      <div className="flex flex-col gap-4">
        <StackedSegmentBar
          size="md"
          aria-label={barAriaLabel ?? 'Duplicated data share of scanned data'}
          segments={[
            { id: 'duplicated', percent: duplicatedPercent, color: duplicatedColor },
            {
              id: 'remainder',
              percent: Math.max(0, 100 - duplicatedPercent),
              color: remainderColor,
            },
          ]}
        />
        <div className="flex items-center gap-6">
          <LegendDotItem
            dotColor={duplicatedColor}
            label={`${duplicatedPercent}% ${duplicatedLegendLabel}`}
            gap={8}
          />
        </div>
      </div>

      {stale != null && stale.percent !== null ? (
        <div data-testid="stale-bar-group" className="flex flex-col gap-4">
          <StackedSegmentBar
            size="md"
            aria-label={stale.barAriaLabel ?? 'Stale data share of all files'}
            segments={[
              { id: 'stale', percent: stale.percent, color: stale.color },
              {
                id: 'stale-remainder',
                percent: Math.max(0, 100 - stale.percent),
                color: remainderColor,
              },
            ]}
          />
          <div className="flex items-center gap-6">
            <LegendDotItem
              dotColor={stale.color}
              label={`${stale.percent}% ${stale.legendLabel}`}
              gap={8}
            />
          </div>
        </div>
      ) : null}

      {/* Equal thirds at any width. A track never narrows below 9.5rem, so on a narrow card
          (1280px, sidebar open) the grid drops to two columns and the third tile wraps below
          instead of overflowing. */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(max(9.5rem,calc((100%_-_4rem)/3)),1fr))] items-start gap-x-8 gap-y-3">
        <MiniMetricItem
          icon={
            <img
              src={illustrations.staleWidgetTotal}
              alt=""
              aria-hidden="true"
              className="size-6"
            />
          }
          value={total.sizeLabel}
          descriptor={{
            dotColor: totalDotColor,
            label: totalDescriptorLabel,
            subValue: total.countLabel,
          }}
        />
        <MiniMetricItem
          icon={
            <img
              src={illustrations.staleWidgetDuplicated}
              alt=""
              aria-hidden="true"
              className="size-6"
            />
          }
          value={duplicated.sizeLabel}
          descriptor={{
            dotColor: duplicatedColor,
            label: duplicatedDescriptorLabel,
            subValue: duplicated.countLabel,
          }}
        />
        {stale ? (
          <MiniMetricItem
            icon={
              <img
                src={illustrations.staleWidgetStale}
                alt=""
                aria-hidden="true"
                className="size-6"
              />
            }
            value={stale.sizeLabel}
            descriptor={{
              dotColor: stale.color,
              label: stale.descriptorLabel,
              subValue: stale.countLabel,
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

StaleDuplicateDataBody.displayName = 'StaleDuplicateDataBody';
